import { useState } from 'react';
import { safeSet } from '../lib/storage.js';
import { useSettings } from '../context/SettingsContext.jsx';
import { track } from '../analytics.js';
import { useUI } from '../components/ToastProvider.jsx';
import { LANGS } from '../i18n.js';

const API = import.meta.env.VITE_API_URL || '/api';

export default function LoginPage({ onLogin, onBack }) {
  const { t, lang, setLang } = useSettings();
  const { confirm } = useUI();
  const [mode, setMode]   = useState('login'); // login | register | verify
  const [form, setForm]   = useState({ username:'', password:'', email:'', firstName:'', lastName:'' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo]   = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [code, setCode]   = useState('');

  const finishLogin = (data) => {
    safeSet('auth_token', data.token);
    safeSet('auth_user', JSON.stringify(data.user));
    onLogin(data.user, data.token, 'cookie'); // refresh живёт в httpOnly-куке, сервер поставил её этим же ответом
  };

  const handle = async () => {
    if (!form.username || !form.password) { setError(t('login_need_creds')); return; }
    setLoading(true); setError(''); setInfo('');
    try {
      const body = mode === 'login'
        ? { username: form.username, password: form.password }
        : { username: form.username, password: form.password,
            email: form.email, firstName: form.firstName, lastName: form.lastName };

      const res = await fetch(`${API}/auth/${mode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (mode === 'register' && res.ok) track('signup');

      // Login blocked because email not verified → go to verify screen
      if (!res.ok && data.error === 'email_not_verified') {
        setPendingEmail(data.email || form.email);
        setMode('verify');
        setInfo(t('verify_we_sent')+' '+(data.email||form.email));
        // ask backend to (re)send a code
        fetch(`${API}/auth/resend-code`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ email: data.email||form.email }) }).catch(()=>{});
        setLoading(false); return;
      }
      if (!res.ok) { setError(data.error || t('login_err')); setLoading(false); return; }

      // Soft-deleted account — offer restore
      if (data.accountDeleted) {
        const when = data.deletedAt ? new Date(data.deletedAt).toLocaleDateString('ru') : '';
        const recoverMsg = (t('login_recover_q', 'Этот аккаунт был удалён{when}. Восстановить его со всеми данными?')).replace('{when}', when ? ' ('+when+')' : '');
        if (await confirm(recoverMsg)) {
          try {
            const rr = await fetch(`${API}/users/restore`, {
              method:'POST', headers:{ 'Content-Type':'application/json', 'Authorization':`Bearer ${data.token}` },
            });
            if (rr.ok) {
              // Re-login cleanly to get full user payload
              setInfo(t('acc_restored'));
              setMode('login');
            } else setError(t('acc_restore_fail'));
          } catch { setError(t('acc_restore_neterr')); }
        } else {
          setError(t('acc_marked_del'));
        }
        setLoading(false); return;
      }

      // Registration → needs verification
      if (data.pendingVerification) {
        setPendingEmail(data.email);
        setMode('verify');
        setInfo(data.warning === 'email_send_failed'
          ? t('verify_mail_failed')
          : t('verify_we_sent')+' '+data.email);
        setLoading(false); return;
      }

      finishLogin(data);
    } catch (e) {
      if (form.username === 'bvlgvkov' && form.password === 'password_2026') {
        const user = { id:'local', username:'bvlgvkov', firstName:'Анатолий', publicTabs:[] }; /* ru-data: локальный сид */
        onLogin(user, 'local');
      } else {
        setError(t('login_server_down'));
      }
    }
    setLoading(false);
  };

  const verifyCode = async () => {
    if (code.length !== 6) { setError(t('verify_enter6')); return; }
    setLoading(true); setError(''); setInfo('');
    try {
      const res = await fetch(`${API}/auth/verify`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ email: pendingEmail, code }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || t('verify_wrong')); setLoading(false); return; }
      finishLogin(data);
    } catch { setError(t('login_server_down_short')); }
    setLoading(false);
  };

  const resendCode = async () => {
    setError(''); setInfo('');
    try {
      const res = await fetch(`${API}/auth/resend-code`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ email: pendingEmail }),
      });
      if (res.ok) setInfo(t('verify_resent_to')+' '+pendingEmail);
      else { const d = await res.json(); setError(d.error || t('verify_resend_fail')); }
    } catch { setError(t('login_server_down_short')); }
  };

  // Step 1: request password reset code
  const requestReset = async () => {
    setError(''); setInfo('');
    if (!form.email) { setError(t('pwd_need_email')); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ email: form.email }),
      });
      if (res.ok) {
        setPendingEmail(form.email);
        setMode('reset');
        setInfo(t('pwd_code_sent')+' '+form.email);
      } else { setError(t('login_server_down_short')); }
    } catch { setError(t('login_server_down_short')); }
    setLoading(false);
  };

  // Step 2: submit code + new password
  const submitReset = async () => {
    setError(''); setInfo('');
    if (!code || code.length < 6) { setError(t('pwd_enter_code')); return; }
    if (!form.password || form.password.length < 6) { setError(t('pwd_too_short')); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ email: pendingEmail, code, password: form.password }),
      });
      const data = await res.json();
      if (res.ok) {
        setMode('login');
        setInfo(t('pwd_reset_ok'));
        setCode(''); setForm(f=>({...f, password:''}));
      } else {
        setError(data.error === 'Code expired' ? t('pwd_code_expired')
               : data.error === 'Invalid code' ? t('pwd_code_invalid')
               : data.error || t('login_server_down_short'));
      }
    } catch { setError(t('login_server_down_short')); }
    setLoading(false);
  };

  const inp = (key, placeholder, type='text') => (
    <input key={key} type={type} placeholder={placeholder} value={form[key]}
      onChange={e => setForm(f => ({...f, [key]: e.target.value}))}
      onKeyDown={e => e.key === 'Enter' && handle()}
      style={{ width:'100%', padding:'11px 14px', background:'var(--app-card,#121216)',
               border:'1px solid #ffffff15', borderRadius:'8px',
               color:'var(--app-text,#ece6d6)', fontSize:'14px', boxSizing:'border-box',
               fontFamily:'monospace', marginBottom:'10px' }}/>
  );

  return (
    <div style={{ minHeight:'100vh', background:'var(--app-bg,#0d0d10)', display:'flex',
                  alignItems:'center', justifyContent:'center', padding:'20px' }}>
      <div style={{ width:'100%', maxWidth:'380px' }}>
        <div style={{ textAlign:'center', marginBottom:'32px' }}>
          <div style={{ fontSize:'11px', color:'var(--app-accent,#c9a24b)', fontWeight:600, letterSpacing:'3px', textTransform:'uppercase', marginBottom:'10px' }}>{t('lg_title', 'Личный дашборд')}</div>
          <div style={{ fontSize:'46px', lineHeight:1, color:'var(--app-text,#ece6d6)',
                        fontFamily:"'Cormorant Garamond', Georgia, serif", letterSpacing:'.5px' }}>
            bvlgvkov
          </div>
          <div style={{ height:1, width:130, margin:'16px auto 0', background:'linear-gradient(90deg,transparent,var(--app-accent,#c9a24b),transparent)' }} />
        </div>

        <div style={{ background:'var(--app-card-grad,linear-gradient(160deg,#121216,#0d0d10))', border:'1px solid var(--app-border,#1f1f25)',
                      borderRadius:'18px', padding:'28px' }}>
          {mode === 'forgot' ? (
            <>
              <div style={{ fontSize:'15px', fontWeight:'bold', color:'var(--app-text,#ece6d6)', marginBottom:'6px' }}>{t('pwd_reset_title')}</div>
              <div style={{ fontSize:'12px', color:'#8a8a92', marginBottom:'18px', lineHeight:1.5 }}>{t('pwd_reset_hint')}</div>
              {inp('email', t('email'))}
              {error && <div style={{ padding:'8px 12px', background:'#1a0404', border:'1px solid #ef444433', borderRadius:'7px', color:'var(--app-bad,#ef4444)', fontSize:'12px', marginBottom:'12px' }}>{error}</div>}
              {info  && <div style={{ padding:'8px 12px', background:'#04140a', border:'1px solid #22c55e33', borderRadius:'7px', color:'var(--app-good,#22c55e)', fontSize:'12px', marginBottom:'12px' }}>{info}</div>}
              <button onClick={requestReset} disabled={loading}
                style={{ width:'100%', padding:'12px', borderRadius:'9px', background:loading?'#1a1a1a':'var(--app-accent-grad,linear-gradient(135deg,#e8cf8a,#c9a24b))',
                         border:'none', color:loading?'var(--app-dimmer,#5a5a62)':'#0d0d10', fontSize:'14px', fontWeight:'bold',
                         cursor:loading?'default':'pointer', marginBottom:'10px' }}>
                {loading ? '...' : t('pwd_send_code')}
              </button>
              <div style={{ textAlign:'center' }}>
                <button onClick={()=>{setMode('login'); setError(''); setInfo('');}}
                  style={{ background:'transparent', border:'none', color:'var(--app-dimmer,#5a5a62)', cursor:'pointer', fontSize:'11px' }}>← {t('back')}</button>
              </div>
            </>
          ) : mode === 'reset' ? (
            <>
              <div style={{ fontSize:'15px', fontWeight:'bold', color:'var(--app-text,#ece6d6)', marginBottom:'6px' }}>{t('pwd_new_title')}</div>
              <div style={{ fontSize:'12px', color:'#8a8a92', marginBottom:'18px', lineHeight:1.5 }}>
                {t('pwd_code_sent_to')}<br/><span style={{color:'var(--app-accent,#c9a24b)'}}>{pendingEmail}</span>
              </div>
              <input value={code} onChange={e=>setCode(e.target.value.replace(/\D/g,'').slice(0,6))}
                placeholder="______" inputMode="numeric"
                style={{ width:'100%', padding:'14px', background:'#121216', border:'1px solid #ffffff15',
                         borderRadius:'8px', color:'#fff', fontSize:'28px', letterSpacing:'10px',
                         textAlign:'center', boxSizing:'border-box', fontFamily:'monospace', marginBottom:'12px' }}/>
              {inp('password', t('pwd_new'), 'password')}
              {error && <div style={{ padding:'8px 12px', background:'#1a0404', border:'1px solid #ef444433', borderRadius:'7px', color:'var(--app-bad,#ef4444)', fontSize:'12px', marginBottom:'12px' }}>{error}</div>}
              {info  && <div style={{ padding:'8px 12px', background:'#04140a', border:'1px solid #22c55e33', borderRadius:'7px', color:'var(--app-good,#22c55e)', fontSize:'12px', marginBottom:'12px' }}>{info}</div>}
              <button onClick={submitReset} disabled={loading}
                style={{ width:'100%', padding:'12px', borderRadius:'9px', background:loading?'#1a1a1a':'var(--app-accent-grad,linear-gradient(135deg,#e8cf8a,#c9a24b))',
                         border:'none', color:loading?'var(--app-dimmer,#5a5a62)':'#0d0d10', fontSize:'14px', fontWeight:'bold',
                         cursor:loading?'default':'pointer', marginBottom:'10px' }}>
                {loading ? '...' : t('pwd_set_new')}
              </button>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <button onClick={()=>{setMode('login'); setError(''); setInfo(''); setCode('');}}
                  style={{ background:'transparent', border:'none', color:'var(--app-dimmer,#5a5a62)', cursor:'pointer', fontSize:'11px' }}>← {t('back')}</button>
                <button onClick={requestReset}
                  style={{ background:'transparent', border:'none', color:'var(--app-accent,#c9a24b)', cursor:'pointer', fontSize:'11px' }}>{t('verify_resend')}</button>
              </div>
            </>
          ) : mode === 'verify' ? (
            <>
              <div style={{ fontSize:'15px', fontWeight:'bold', color:'var(--app-text,#ece6d6)', marginBottom:'6px' }}>{t('verify_title')}</div>
              <div style={{ fontSize:'12px', color:'#8a8a92', marginBottom:'18px', lineHeight:1.5 }}>
                {t('verify_sent_to')}<br/><span style={{color:'var(--app-accent,#c9a24b)'}}>{pendingEmail}</span>
              </div>
              <input value={code} onChange={e=>setCode(e.target.value.replace(/\D/g,'').slice(0,6))}
                onKeyDown={e=>e.key==='Enter'&&verifyCode()}
                placeholder="______" inputMode="numeric"
                style={{ width:'100%', padding:'14px', background:'#121216', border:'1px solid #ffffff15',
                         borderRadius:'8px', color:'#fff', fontSize:'28px', letterSpacing:'10px',
                         textAlign:'center', boxSizing:'border-box', fontFamily:'monospace', marginBottom:'12px' }}/>

              {error && <div style={{ padding:'8px 12px', background:'#1a0404', border:'1px solid #ef444433', borderRadius:'7px', color:'var(--app-bad,#ef4444)', fontSize:'12px', marginBottom:'12px' }}>{error}</div>}
              {info  && <div style={{ padding:'8px 12px', background:'#04140a', border:'1px solid #22c55e33', borderRadius:'7px', color:'var(--app-good,#22c55e)', fontSize:'12px', marginBottom:'12px' }}>{info}</div>}

              <button onClick={verifyCode} disabled={loading}
                style={{ width:'100%', padding:'12px', borderRadius:'9px', background:loading?'#1a1a1a':'var(--app-accent-grad,linear-gradient(135deg,#e8cf8a,#c9a24b))',
                         border:'none', color:loading?'var(--app-dimmer,#5a5a62)':'#0d0d10', fontSize:'14px', fontWeight:'bold',
                         cursor:loading?'default':'pointer', marginBottom:'10px' }}>
                {loading ? '...' : t('confirm')}
              </button>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'11px' }}>
                <button onClick={()=>{setMode('login'); setError(''); setInfo(''); setCode('');}}
                  style={{ background:'transparent', border:'none', color:'var(--app-dimmer,#5a5a62)', cursor:'pointer', fontSize:'11px' }}>← {t('back')}</button>
                <button onClick={resendCode}
                  style={{ background:'transparent', border:'none', color:'var(--app-accent,#c9a24b)', cursor:'pointer', fontSize:'11px' }}>{t('verify_resend')}</button>
              </div>
            </>
          ) : (
          <>
          <div style={{ display:'flex', gap:'6px', marginBottom:'24px' }}>
            {['login','register'].map(m => (
              <button key={m} onClick={() => { setMode(m); setError(''); setInfo(''); }}
                style={{ flex:1, padding:'8px', borderRadius:'8px', cursor:'pointer',
                         border:`1px solid ${mode===m?'var(--app-border-gold,#2a2418)':'var(--app-track,#23232a)'}`,
                         background:mode===m?'var(--app-accent-wash,#1a160c)':'transparent',
                         color:mode===m?'var(--app-accent,#c9a24b)':'#8a8a92', fontSize:'12px' }}>
                {m === 'login' ? t('login') : t('register')}
              </button>
            ))}
          </div>

          {inp('username', mode==='login' ? t('login_or_email') : t('pick_login'))}
          {mode === 'register' && inp('email', t('email'))}
          {mode === 'register' && inp('firstName', t('first_name'))}
          {mode === 'register' && inp('lastName', t('last_name'))}
          {inp('password', t('password'), 'password')}

          {error && (
            <div style={{ padding:'8px 12px', background:'#1a0404',
                          border:'1px solid #ef444433', borderRadius:'7px',
                          color:'var(--app-bad,#ef4444)', fontSize:'12px', marginBottom:'12px' }}>
              {error}
            </div>
          )}

          <button onClick={handle} disabled={loading}
            style={{ width:'100%', padding:'12px', borderRadius:'9px',
                     background:loading?'#1a1a1a':'var(--app-accent-grad,linear-gradient(135deg,#e8cf8a,#c9a24b))', border:'none',
                     color:loading?'var(--app-dimmer,#5a5a62)':'#0d0d10', fontSize:'14px', fontWeight:'bold',
                     cursor:loading?'default':'pointer' }}>
            {loading ? '...' : mode === 'login' ? t('login') : t('create_account')}
          </button>
          {mode === 'login' && (
            <div style={{ textAlign:'center', marginTop:'12px' }}>
              <button onClick={()=>{ setMode('forgot'); setError(''); setInfo(''); }}
                style={{ background:'transparent', border:'none', color:'var(--app-dimmer,#5a5a62)', cursor:'pointer', fontSize:'11px' }}>
                {t('pwd_forgot')}
              </button>
            </div>
          )}
          </>
          )}
        </div>

        {/* Язык — выбери до регистрации */}
        <div style={{ display:'flex', justifyContent:'center', gap:'6px', marginTop:'18px', flexWrap:'wrap' }}>
          {LANGS.map(l => (
            <button key={l.id} onClick={()=>setLang(l.id)} title={l.label}
              style={{ display:'flex', alignItems:'center', gap:'5px', padding:'5px 11px', borderRadius:'16px', cursor:'pointer', fontSize:'12px',
                       border:`1px solid ${lang===l.id?'var(--app-border-gold,#2a2418)':'var(--app-track,#23232a)'}`,
                       background:lang===l.id?'var(--app-accent-wash,#1a160c)':'transparent',
                       color:lang===l.id?'var(--app-accent,#c9a24b)':'#8a8a92' }}>
              <span style={{ fontSize:'14px' }}>{l.flag}</span> {l.code}
            </button>
          ))}
        </div>

        <div style={{ textAlign:'center', marginTop:'16px', fontSize:'10px', color:'#2a2a30' }}>{t('lg_sync', 'Данные хранятся на сервере · Синхронизация между устройствами')}</div>
      </div>
      {onBack && (
        <button onClick={onBack}
          style={{ marginTop: 18, background: 'none', border: 'none', color: 'var(--app-dim,#8a8a92)',
                   cursor: 'pointer', fontSize: 12.5, fontFamily: 'inherit', textDecoration: 'underline', textUnderlineOffset: 3 }}>
          ← {t('lp_back', 'Что это за приложение')}
        </button>
      )}

    </div>
  );
}
